import {
    Action,
    IAgentRuntime,
    Memory,
    State,
    HandlerCallback,
    elizaLogger,
} from "@elizaos/core";
import { initCotiWalletProvider } from "../providers/wallet";
import { ethers } from "ethers";
import { z } from "zod";

// Schema for transfer parameters
export const TransferPrivateERC721Schema = z.object({
    tokenAddress: z.string().describe("ERC721 token contract address"),
    recipientAddress: z.string().describe("Recipient COTI address"),
    tokenId: z.string().describe("ID of the NFT token to transfer"),
    useSafeTransfer: z.boolean().optional().describe("Use safeTransferFrom instead of transferFrom"),
    gasLimit: z.number().optional().describe("Optional gas limit"),
});

export type TransferPrivateERC721Params = z.infer<typeof TransferPrivateERC721Schema>;

// Basic ERC721 ABI for transfers
const ERC721_ABI = [
    "function transferFrom(address from, address to, uint256 tokenId)",
    "function safeTransferFrom(address from, address to, uint256 tokenId)",
    "function ownerOf(uint256 tokenId) view returns (address)",
    "function approve(address to, uint256 tokenId)",
    "function getApproved(uint256 tokenId) view returns (address)"
];

export const transferPrivateErc721TokenAction: Action = {
    name: "TRANSFER_PRIVATE_ERC721_TOKEN",
    similes: [
        "TRANSFER_ERC721",
        "TRANSFER_NFT",
        "SEND_NFT",
        "TRANSFER_PRIVATE_NFT",
        "SEND_TOKEN",
        "TRANSFER_TOKEN"
    ],
    description: "Transfer a private ERC721 NFT token to another address on the COTI blockchain.",
    validate: async (runtime: IAgentRuntime, message: Memory): Promise<boolean> => {
        try {
            const text = message.content?.text || "";
            elizaLogger.info(`TRANSFER_PRIVATE_ERC721_TOKEN validation - text: "${text}"`);
            
            // Check for transfer keywords
            const isTransferQuery = /\b(transfer|send).*\b(nft|token)\b/i.test(text);
            elizaLogger.info(`TRANSFER_PRIVATE_ERC721_TOKEN validation - isTransferQuery: ${isTransferQuery}`);
            
            // Check if message content has required fields
            if (message.content?.tokenAddress && message.content?.recipientAddress && message.content?.tokenId) {
                elizaLogger.info(`TRANSFER_PRIVATE_ERC721_TOKEN validation - structured params found`);
                return true;
            }

            // Check if text contains addresses and token ID
            const addressMatches = text.match(/0x[a-fA-F0-9]{40}/g);
            const tokenIdMatch = text.match(/token\s*id\s*(\d+)|id\s*(\d+)/i);
            
            elizaLogger.info(`TRANSFER_PRIVATE_ERC721_TOKEN validation - addresses found: ${addressMatches?.length || 0}`);
            elizaLogger.info(`TRANSFER_PRIVATE_ERC721_TOKEN validation - tokenId found: ${!!(tokenIdMatch?.[1] || tokenIdMatch?.[2])}`);

            if (isTransferQuery && addressMatches && addressMatches.length >= 2 && tokenIdMatch) {
                elizaLogger.info(`TRANSFER_PRIVATE_ERC721_TOKEN validation - returning true`);
                return true;
            }

            elizaLogger.info(`TRANSFER_PRIVATE_ERC721_TOKEN validation - returning false`);
            return false;
        } catch (error) {
            elizaLogger.error(`TRANSFER_PRIVATE_ERC721_TOKEN validation error:`, error);
            return false;
        }
    },
    handler: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State | undefined,
        options: any = {},
        callback?: HandlerCallback
    ) => {
        try {
            elizaLogger.info("Starting TRANSFER_PRIVATE_ERC721_TOKEN action");

            let params: TransferPrivateERC721Params;

            // Try to get parameters from message content
            if (message.content?.tokenAddress && message.content?.recipientAddress && message.content?.tokenId) {
                params = TransferPrivateERC721Schema.parse(message.content);
            } else {
                // Extract from message text
                const text = message.content?.text || "";
                const addressMatches = text.match(/0x[a-fA-F0-9]{40}/g);
                const tokenIdMatch = text.match(/token\s*id\s*(\d+)|id\s*(\d+)/i);
                
                if (!addressMatches || addressMatches.length < 2 || !tokenIdMatch) {
                    throw new Error("Could not extract required parameters from message");
                }
                
                params = {
                    tokenAddress: addressMatches[0],
                    recipientAddress: addressMatches[1],
                    tokenId: tokenIdMatch[1] || tokenIdMatch[2],
                    useSafeTransfer: false
                };
            }

            const walletProvider = initCotiWalletProvider(runtime);
            const wallet = walletProvider.getWallet();
            const fromAddress = walletProvider.getAddress();
            const provider = new ethers.providers.JsonRpcProvider(process.env.COTI_RPC_URL || "https://testnet.coti.io/rpc");

            // Create contract instance for reading
            const contractReader = new ethers.Contract(
                params.tokenAddress,
                ERC721_ABI,
                provider
            );



            elizaLogger.info(`Transferring NFT token ${params.tokenId} from ${fromAddress} to ${params.recipientAddress}`);

            // Check if we own the token
            const owner = await contractReader.ownerOf(params.tokenId);
            if (owner.toLowerCase() !== fromAddress.toLowerCase()) {
                throw new Error(`You don't own token ${params.tokenId}. Current owner: ${owner}`);
            }

            // Transfer the token
            const transferMethod = params.useSafeTransfer ? "safeTransferFrom" : "transferFrom";
            const contractInterface = new ethers.utils.Interface(ERC721_ABI);
            const data = contractInterface.encodeFunctionData(transferMethod, [
                fromAddress,
                params.recipientAddress,
                params.tokenId
            ]);

            const tx = await wallet.sendTransaction({
                to: params.tokenAddress,
                data: data,
                gasLimit: params.gasLimit || 300000
            });

            const receipt = await tx.wait();

            const responseText = `Successfully transferred private ERC721 NFT!
Token ID: ${params.tokenId}
Contract: ${params.tokenAddress}
From: ${fromAddress}
To: ${params.recipientAddress}
Transaction Hash: ${tx.hash}`;

            if (callback) {
                await callback({
                    text: responseText,
                    content: {
                        success: true,
                        tokenId: params.tokenId,
                        tokenAddress: params.tokenAddress,
                        from: fromAddress,
                        to: params.recipientAddress,
                        transactionHash: tx.hash,
                    },
                });
            }

            return {
                text: responseText,
                success: true,
                data: {
                    tokenId: params.tokenId,
                    tokenAddress: params.tokenAddress,
                    from: fromAddress,
                    to: params.recipientAddress,
                    transactionHash: tx.hash,
                },
            };
        } catch (error) {
            elizaLogger.error("Error in TRANSFER_PRIVATE_ERC721_TOKEN action:", error);
            const errorMessage = `Failed to transfer private ERC721 token: ${error instanceof Error ? error.message : String(error)}`;

            if (callback) {
                await callback({
                    text: errorMessage,
                    content: { success: false, error: errorMessage },
                });
            }

            return {
                text: errorMessage,
                success: false,
                error: error instanceof Error ? error : new Error(String(error)),
            };
        }
    },
    examples: [
        [
            {
                name: "{{user1}}",
                content: {
                    text: "Transfer NFT token ID 123 to bob",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "I'll transfer that NFT to the specified address.",
                    action: "TRANSFER_PRIVATE_ERC721_TOKEN",
                },
            },
        ],
    ],
};
